import { aiActionBilling, expectedAiBillingMode, finishAiAction, isAiGenerationRequest } from '@/domain/ai-action-billing'
import { notifyAiQuotaExhaustion } from '@/domain/ai-quota-notice'
import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { env } from '@/config/env'
import {
  prepareDiagnosticsRequest,
  recordDiagnosticsError,
  recordDiagnosticsResponse,
} from '@/services/diagnostics/bug-report-diagnostics'
import { extractApiErrorMessage } from '@/utils/api-error'
import { requestAiComplianceAcceptance } from '@/domain/ai-compliance-events'

const ADMIN_LOCALE_KEY = 'tb_admin_ui_locale'

function readAdminLocale(): string | null {
  try {
    const raw = localStorage.getItem(ADMIN_LOCALE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { code?: unknown } | string
    const value = typeof parsed === 'string' ? parsed : parsed?.code
    return typeof value === 'string' && /^(de|en|fr|es|it|pt)$/.test(value) ? value : null
  } catch {
    return null
  }
}

const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
  withCredentials: true,
})

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  config.headers['X-Ai-Billing-Mode'] ||= expectedAiBillingMode()
  if (isAiGenerationRequest(config.url || '', config.method)) {
    (config as InternalAxiosRequestConfig & { aiActionId?: number }).aiActionId ??= aiActionBilling.activeAction.value?.id
  }
  prepareDiagnosticsRequest(config)
  if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
    const headers = config.headers as unknown as Record<string, unknown> & {
      delete?: (header: string) => void
    }
    headers.delete?.('Content-Type')
    headers.delete?.('content-type')
    delete headers['Content-Type']
    delete headers['content-type']
  }
  const { useAuthStore } = await import('@/stores/auth')
  const token = useAuthStore().token
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const adminLocale = readAdminLocale()
  if (adminLocale && config.headers) {
    config.headers['X-Admin-Locale'] = adminLocale
  }
  return config
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (v?: string | null) => void
  reject: (e?: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error)
    else p.resolve(token)
  })
  failedQueue = []
}

function isStaffLoginRequest(config: InternalAxiosRequestConfig): boolean {
  const url = config.url || ''
  return (
    config.method?.toLowerCase() === 'post'
    && url.includes('/api/v1/authentication/staff')
  )
}

function shouldShowGlobalErrorToast(error: unknown, config: (InternalAxiosRequestConfig & { skipGlobalErrorToast?: boolean }) | undefined): boolean {
  if (!config || config.skipGlobalErrorToast) return false
  const profileCode = (error as { response?: { data?: { data?: { code?: string } } } })?.response?.data?.data?.code
  if (profileCode === 'PROFILE_CONFIRMATION_REQUIRED') return false
  if (isStaffLoginRequest(config)) return false
  if (config.url?.includes('/api/v1/auth-refresh')) return false

  const method = String(config.method || 'get').toLowerCase()
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return false

  const status = error && typeof error === 'object'
    ? Number((error as { response?: { status?: number } }).response?.status ?? 0)
    : 0
  if (status === 401) return false
  return status === 0 || status >= 400
}

async function showGlobalErrorToast(error: unknown): Promise<void> {
  const message = extractApiErrorMessage(error, 'Die Anfrage ist fehlgeschlagen.')
  if (!message) return
  const { useToastStore } = await import('@/stores/toast')
  useToastStore().error(message)
}

apiClient.interceptors.response.use(
  (res) => {
    const config = res.config as InternalAxiosRequestConfig & { skipGlobalErrorToast?: boolean }
    if (!config.skipGlobalErrorToast && res.data?.error) notifyAiQuotaExhaustion(res.data.error)
    finishAiAction((config as typeof config & { aiActionId?: number }).aiActionId)
    recordDiagnosticsResponse(res)
    return res
  },
  async (error) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      _aiComplianceRetry?: boolean
      skipAiCompliancePrompt?: boolean
      skipGlobalErrorToast?: boolean
    }
    const appCode = error?.response?.data?.data?.appCode ?? error?.response?.data?.appCode
    if (
      appCode === 'AI_COMPLIANCE_ACCEPTANCE_REQUIRED'
      && original
      && !original.skipAiCompliancePrompt
      && !original._aiComplianceRetry
      && typeof window !== 'undefined'
    ) {
      try {
        await requestAiComplianceAcceptance()
        original._aiComplianceRetry = true
        return apiClient(original)
      } catch {
        return Promise.reject(error)
      }
    }
    finishAiAction((original as typeof original & { aiActionId?: number })?.aiActionId)
    recordDiagnosticsError(error)
    const quotaHandled = !original?.skipGlobalErrorToast && notifyAiQuotaExhaustion(error)
    if (!quotaHandled && shouldShowGlobalErrorToast(error, original)) {
      void showGlobalErrorToast(error)
    }

    if (error.response?.status !== 401 || !original) {
      return Promise.reject(error)
    }

    if (original.url?.includes('/api/v1/auth-refresh')) {
      await invalidateSessionAndRedirect()
      return Promise.reject(error)
    }

    if (isStaffLoginRequest(original)) {
      return Promise.reject(error)
    }

    if (original._retry) {
      await invalidateSessionAndRedirect()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then((token) => {
          if (original.headers && token) {
            original.headers.Authorization = `Bearer ${token}`
          }
          return apiClient(original)
        })
        .catch((err) => Promise.reject(err))
    }

    original._retry = true
    isRefreshing = true
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()

    try {
      await authStore.refreshAccessToken()
      const newToken = authStore.token
      processQueue(null, newToken || null)
      if (original.headers && newToken) {
        original.headers.Authorization = `Bearer ${newToken}`
      }
      return apiClient(original)
    } catch (refreshErr) {
      processQueue(refreshErr, null)
      await invalidateSessionAndRedirect()
      return Promise.reject(refreshErr)
    } finally {
      isRefreshing = false
    }
  },
)

async function invalidateSessionAndRedirect() {
  const { useAuthStore } = await import('@/stores/auth')
  useAuthStore().clear()
  redirectLogin()
}

function redirectLogin() {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
  const path = `${base}/login`.replace(/\/+/g, '/')
  if (!window.location.pathname.endsWith('/login')) {
    window.location.assign(path)
  }
}

export default apiClient
